import "./App.css";
import React, { useEffect } from 'react';
import {Route, Routes, useNavigate} from 'react-router-dom';
import { ProtectedRoute } from "./routes";
import AuthCheck from "./utils/authCheck";

const HomePage = React.lazy(()=> import('./pages/home'));
const LoginPage = React.lazy(()=> import('./pages/login'));
const RecipePage = React.lazy(()=> import('./pages/recipes/recipeList'))
const ReciepAddPage = React.lazy(()=> import('./pages/recipes/recipeAdd'))
const RecipeEditPage = React.lazy(()=> import('./pages/recipes/recipeEdit'))
const UserPage = React.lazy(()=> import('./pages/users/usersList'))
const UserAddPage = React.lazy(()=> import('./pages/users/usersAdd'))
const UserEditPage = React.lazy(()=> import('./pages/users/usersEdit'))
const CategoryPage = React.lazy(()=> import('./pages/categories/categoryList'))
const CategoryAddPage = React.lazy(()=> import('./pages/categories/categoryAdd'))
const CategoryEditPage = React.lazy(()=> import('./pages/categories/categoryEdit'))
const ProductPage = React.lazy(()=> import('./pages/products/productsList'))
const ProductAddPage = React.lazy(()=> import('./pages/products/productAdd'))
const ProductEditPage = React.lazy(()=> import('./pages/products/productEdit'))
const InvoicePage = React.lazy(()=> import('./pages/invoice/invoiceAdd'))
const OrdersViewPage = React.lazy(()=> import('./pages/invoice/orderList'))
const StockPage = React.lazy(()=> import('./pages/stock'))
const UserStatsPage = React.lazy(()=> import('./pages/users/userStats'))
const GeneralReportPage = React.lazy(()=> import('./pages/reports'))
const StoreProductPage = React.lazy(()=> import('./pages/Inventory/storeProducts'))
const StoreProductAddPage = React.lazy(()=> import('./pages/Inventory/storeProductAdd'))
const ExpensesPage = React.lazy(()=> import('./pages/Inventory/expenses'))
const ExpensesAddPage = React.lazy(()=> import('./pages/Inventory/expensesAdd'))
const CuratedListPage = React.lazy(()=> import('./pages/curated/curatedList'))
const CuratedAddPage = React.lazy(()=> import('./pages/curated/curatedAdd'))
const CuratedEditPage = React.lazy(()=> import('./pages/curated/curatedEdit'))
const CartListPage = React.lazy(()=> import('./pages/cart/cartList'))
const OrderListPage = React.lazy(()=> import('./pages/orders/orderList'))
const OrderAnalyticsPage = React.lazy(()=> import('./pages/orders/orderAnalytics'))
const BlogListPage = React.lazy(()=> import('./pages/blog/blogList'))
const BlogAddPage = React.lazy(()=> import('./pages/blog/blogAdd'))
const BlogEditPage = React.lazy(()=> import('./pages/blog/blogEdit'))
const CookingClassListPage = React.lazy(()=> import('./pages/cooking-class/cookingClassList'))
const CookingClassAddPage = React.lazy(()=> import('./pages/cooking-class/cookingClassAdd'))
const CookingClassEditPage = React.lazy(()=> import('./pages/cooking-class/cookingClassEdit'))
const ReviewsListPage = React.lazy(()=> import('./pages/reviews/reviewsList'))
const DiscountListPage = React.lazy(()=> import('./pages/discounts/discountList'))
const DiscountDetailPage = React.lazy(()=> import('./pages/discounts/discountDetail'))
const CustomerListPage = React.lazy(()=> import('./pages/customers/customerList'))
const CustomerDetailPage = React.lazy(()=> import('./pages/customers/customerDetail'))
const GeographicReportPage = React.lazy(()=> import('./pages/reports/geographicReport'))
const WholesaleListPage = React.lazy(()=> import('./pages/wholesale/wholesaleList'))
const WholesaleOrderListPage = React.lazy(()=> import('./pages/wholesale/wholesaleOrderList'))
const WholesaleOrderDetailPage = React.lazy(()=> import('./pages/wholesale/wholesaleOrderDetail'))

const ErrorPage = React.lazy(()=> import('./pages/error'))

const App = () => {
  const router = useNavigate()

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'full_name' || e.key === 'role') {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('full_name');
        sessionStorage.removeItem('role');
        router('/login')
        return
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  },[])

  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route path='/' element={<HomePage/>}></Route>
        <Route path='/users/list' element={<UserPage/>}></Route>
        <Route path='/users/add' element={<UserAddPage/>}></Route>
        <Route path='/users/edit/:userId' element={<UserEditPage/>}></Route>
        <Route path='/products/list' element={<ProductPage/>}></Route>
        <Route path='/products/add' element={<ProductAddPage/>}></Route>
        <Route path='/category/list' element={<CategoryPage/>}></Route>
        <Route path='/category/add' element={<CategoryAddPage/>}></Route>
        <Route path='/recipe/list' element={<RecipePage/>}></Route>
        <Route path='/products/edit/:productId' element={<ProductEditPage/>}></Route>
        <Route path='/category/edit/:categoryId' element={<CategoryEditPage/>}></Route>
        <Route path='/recipe/add' element={<ReciepAddPage/>}></Route>
        <Route path='/recipe/edit/:recipeId' element={<RecipeEditPage/>}></Route>
        <Route path='/curated/list' element={<CuratedListPage/>}></Route>
        <Route path='/curated/add' element={<CuratedAddPage/>}></Route>
        <Route path='/curated/edit/:curatedId' element={<CuratedEditPage/>}></Route>
        <Route path='/cart/list' element={<CartListPage/>}></Route>
        <Route path='/orders/list' element={<OrderListPage/>}></Route>
        <Route path='/orders/analytics' element={<OrderAnalyticsPage/>}></Route>
        <Route path='/blog/list' element={<BlogListPage/>}></Route>
        <Route path='/blog/add' element={<BlogAddPage/>}></Route>
        <Route path='/blog/edit/:blogId' element={<BlogEditPage/>}></Route>

        <Route path='/cooking-class/list' element={<CookingClassListPage/>}></Route>
        <Route path='/cooking-class/add' element={<CookingClassAddPage/>}></Route>
        <Route path='/cooking-class/edit/:classId' element={<CookingClassEditPage/>}></Route>

        <Route path='/reviews/list' element={<ReviewsListPage/>}></Route>

        <Route path='/discounts/list' element={<DiscountListPage/>}></Route>
        <Route path='/discounts/detail/:discountId' element={<DiscountDetailPage/>}></Route>

        <Route path='/customers/list' element={<CustomerListPage/>}></Route>
        <Route path='/customers/detail/:email' element={<CustomerDetailPage/>}></Route>

        <Route path='/reports/geographic' element={<GeographicReportPage/>}></Route>

        <Route path='/wholesale/list' element={<WholesaleListPage/>}></Route>
        <Route path='/wholesale/orders' element={<WholesaleOrderListPage/>}></Route>
        <Route path='/wholesale/orders/:id' element={<WholesaleOrderDetailPage/>}></Route>
      </Route>
      
      <Route path="/login" element={<LoginPage/>}></Route>
      <Route path="/error" element={<ErrorPage/>}></Route>
    </Routes>
  );
}

export default App;
